import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { ThemeProvider } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { createTheme } from '@mui/material/styles';
import { fetchJson } from '../apiBase';
import ParentStudentPageLayout from './ParentStudentPageLayout';

const paymentTheme = createTheme({
  palette: { primary: { main: '#0f766e' }, text: { primary: '#0f1e56', secondary: '#52658b' }, background: { paper: '#fff' } },
  typography: { fontFamily: 'Nunito, Inter, system-ui, sans-serif', h4: { fontWeight: 900 }, h6: { fontWeight: 900 } },
  components: {
    MuiCard: { styleOverrides: { root: { border: '1px solid #d6e8ed', borderRadius: 20, boxShadow: '0 8px 22px rgba(29,65,107,0.06)' } } },
    MuiButton: { styleOverrides: { root: { borderRadius: 10, fontWeight: 800, textTransform: 'none' } } },
  },
});

const lira = (value) => `${Number(value || 0).toLocaleString('tr-TR')} TL`;
const day = (value) => value ? new Date(`${String(value).slice(0, 10)}T12:00:00`).toLocaleDateString('tr-TR') : '—';
const periodLabel = (value) => value ? new Date(`${value}T12:00:00`).toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' }) : '';

export default function DanduPaymentsPage() {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState(new URLSearchParams(window.location.search).get('student_id') || '');
  const [data, setData] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [invoice, setInvoice] = useState({ name: '', identity_number: '', address: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = async (sid) => {
    if (!sid) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetchJson(`/parent/dandu-payments?student_id=${encodeURIComponent(sid)}`);
      if (!response.ok) throw new Error(response.status === 404 ? 'Bu öğrenci DANDU ödemelerine dahil değil.' : 'Ödeme bilgileri yüklenemedi.');
      const payload = await response.json();
      setData(payload);
      setSelectedPlan(payload.plan || '');
      setInvoice({ name: payload.invoice_name || '', identity_number: payload.invoice_identity_number || '', address: payload.invoice_address || '' });
    } catch (err) {
      setData(null);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetchJson('/parent/dandu-payments/students').then(async (response) => {
      if (!response.ok) throw new Error();
      const body = await response.json();
      if (cancelled) return;
      const items = body.items || [];
      setStudents(items);
      const requestedStudentId = new URLSearchParams(window.location.search).get('student_id') || '';
      const requested = items.find((item) => String(item.student_id) === requestedStudentId);
      const sid = requested ? requestedStudentId : String(items[0]?.student_id || '');
      setStudentId(sid);
      if (sid) load(sid);
    }).catch(() => { if (!cancelled) setError('Öğrenci bilgileri yüklenemedi.'); });
    return () => { cancelled = true; };
  }, []); // initial selection only

  const act = async (path, method, body) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await fetchJson(path, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!response.ok) throw new Error('İşlem tamamlanamadı. Lütfen tekrar deneyin.');
      const payload = await response.json();
      setData(payload);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const showInvoice = async (periodId) => {
    setError('');
    try {
      const response = await fetchJson(`/parent/dandu-payments/invoices/${periodId}?student_id=${encodeURIComponent(studentId)}`);
      if (!response.ok) throw new Error('Fatura açılamadı.');
      const url = URL.createObjectURL(await response.blob());
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { setError(err.message); }
  };

  const pending = (data?.periods || []).filter((p) => p.status === 'PAYMENT_PENDING' || p.status === 'PAST_DUE');
  const paid = (data?.periods || []).filter((p) => p.status === 'PAID').reverse();
  const selectedStudent = students.find((student) => String(student.student_id) === String(studentId));

  return <ParentStudentPageLayout studentId={studentId} studentFallback={selectedStudent} students={students} fetchPointBalance activePage="payments">
    <ThemeProvider theme={paymentTheme}><Box sx={{ color: '#0f1e56' }}><Container maxWidth="sm" disableGutters>
    <Stack spacing={2}>
      <Typography variant="h4" fontWeight={900}>Aboneliğim</Typography>
      <Typography color="text.secondary">DANDU üyeliğinizi ve faturalarınızı buradan takip edebilirsiniz.</Typography>
      {!busy && !students.length && !error && <Alert severity="info">DANDU ödemelerine dahil bir öğrenciniz bulunmuyor.</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}
      {busy && <CircularProgress size={24} />}
      {data && <>
        <Card><CardContent><Typography variant="h6" fontWeight={700}>{data.student_name} için DANDU üyeliği</Typography>
          {data.plan ? <Typography sx={{ mt: 1 }}>{data.plan === 'ANNUAL' ? 'Yıllık' : 'Aylık'} plan · {lira(data.price)} / {data.plan === 'ANNUAL' ? 'yıl' : 'ay'}</Typography> : <>
            <Typography sx={{ mt: 2, mb: 1 }}>Size uygun planı seçin. İki plan da aynı DANDU erişimini sağlar.</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant={selectedPlan === 'MONTHLY' ? 'contained' : 'outlined'} onClick={() => setSelectedPlan('MONTHLY')}>Aylık · 50 TL</Button>
              <Button variant={selectedPlan === 'ANNUAL' ? 'contained' : 'outlined'} onClick={() => setSelectedPlan('ANNUAL')}>Yıllık · 500 TL · 2 ay avantajlı</Button>
            </Stack>
            <Button sx={{ mt: 2 }} variant="contained" disabled={!selectedPlan || busy} onClick={() => act('/parent/dandu-payments/plan', 'POST', { student_id: Number(studentId), plan: selectedPlan })}>Devam et</Button>
          </>}
          {data.plan && <Typography color="text.secondary" sx={{ mt: 1 }}>Sonraki dönem: {day(data.next_due_date)}</Typography>}
        </CardContent></Card>
        {data.plan && pending.length > 0 && <Card><CardContent>
          <Typography variant="h6" fontWeight={700}>Bekleyen toplam: {lira(data.pending_total)}</Typography>
          <Stack sx={{ my: 1 }} spacing={0.5}>{pending.map((period) => <Typography key={period.id}>{periodLabel(period.period_start)} · {lira(period.amount)}{period.payment_claimed_at ? ' · Bildiriminiz alındı' : ''}</Typography>)}</Stack>
          <Divider sx={{ my: 2 }} />
          {data.payment_details.ready ? <>
            <Typography fontWeight={700}>FAST / havale bilgileri</Typography>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
              <Typography sx={{ overflowWrap: 'anywhere' }}>Alıcı: {data.payment_details.name}</Typography>
              <Button size="small" onClick={() => navigator.clipboard.writeText(data.payment_details.name)}>Adı kopyala</Button>
            </Stack>
            <Typography sx={{ mt: 1, overflowWrap: 'anywhere' }}>IBAN: {data.payment_details.iban}</Typography>
            <Button onClick={() => navigator.clipboard.writeText(data.payment_details.iban)}>IBAN'ı kopyala</Button>
            <Typography sx={{ mt: 1 }}>Ödeme açıklama metni: <strong>{data.payment_reference}</strong></Typography>
            <Button onClick={() => navigator.clipboard.writeText(data.payment_reference)}>Açıklama metnini kopyala</Button>
            <Typography color="text.secondary">Ödemenizi eşleştirebilmemiz için banka transferinin açıklama alanına bu metni aynen yazın. 🌿</Typography>
            {data.is_payer && <Button variant="contained" sx={{ mt: 2 }} disabled={busy} onClick={async () => { if (await act('/parent/dandu-payments/claim', 'POST', { student_id: Number(studentId) })) setMessage('Ödeme bildiriminizi aldık. Banka hareketini kontrol ettikten sonra bilgi vereceğiz.'); }}>Ödemeyi yaptım</Button>}
          </> : <Alert severity="info">Banka bilgileri yakında paylaşılacak. Şu anda ödeme yapmanız gerekmiyor.</Alert>}
        </CardContent></Card>}
        {data.plan && <Card><CardContent><Typography variant="h6" fontWeight={700}>Ödeme geçmişim</Typography>
          {paid.length ? paid.map((period) => <Box key={period.id} sx={{ py: 1 }}><Typography>{periodLabel(period.period_start)} · {lira(period.amount)} · Ödendi</Typography><Typography variant="body2" color="text.secondary">Ödeme tarihi: {day(period.paid_at)}</Typography>
            {period.has_invoice_file ? <Button onClick={() => showInvoice(period.id)}>Faturayı görüntüle</Button> : <Typography variant="body2" color="text.secondary">Fatura hazırlanıyor</Typography>}
          </Box>) : <Typography color="text.secondary" sx={{ mt: 1 }}>Henüz onaylanmış ödemeniz bulunmuyor.</Typography>}
        </CardContent></Card>}
        {data.is_payer && <Card><CardContent><Typography variant="h6" fontWeight={700}>İsteğe bağlı fatura bilgileri</Typography>
          <Typography color="text.secondary" sx={{ my: 1 }}>T.C. kimlik numarası ve adres yalnızca faturalama için istenir; ödeme için zorunlu değildir.</Typography>
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            <TextField label="Ad soyad" value={invoice.name} onChange={(e) => setInvoice({ ...invoice, name: e.target.value })} />
            <TextField label="T.C. kimlik numarası" value={invoice.identity_number} inputProps={{ maxLength: 11, inputMode: 'numeric' }} onChange={(e) => setInvoice({ ...invoice, identity_number: e.target.value.replace(/\D/g, '') })} />
            <TextField label="Adres" multiline minRows={2} value={invoice.address} onChange={(e) => setInvoice({ ...invoice, address: e.target.value })} />
            <Button disabled={busy || (invoice.identity_number && invoice.identity_number.length !== 11)} onClick={async () => { if (await act('/parent/dandu-payments/invoice-info', 'PUT', { student_id: Number(studentId), name: invoice.name || null, identity_number: invoice.identity_number || null, address: invoice.address || null })) setMessage('Fatura bilgileriniz kaydedildi.'); }}>Bilgileri kaydet</Button>
          </Stack>
        </CardContent></Card>}
      </>}
    </Stack>
  </Container></Box></ThemeProvider>
  </ParentStudentPageLayout>;
}
